import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import type { TimeFormat } from '@/core/stores/ui.store';
import { Input } from '@/shared/components/ui/input';
import { TimeSelect } from '@/shared/components/ui/TimeSelect';

interface DateTimeInputGroupProps {
  /** ISO date value used for active-field highlight */
  field: 'start' | 'end';
  activeField: 'start' | 'end';
  dateTestId: string;
  timeTestId: string;
  displayDate: string;
  /** Canonical `HH:mm` (24h) time value, or empty when no time is set. */
  time: string;
  dateFormat: string;
  timeFormat: TimeFormat;
  disabled?: boolean;
  includeTime: boolean;
  timeLabelKey: string;
  onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDateBlur: () => void;
  onDateFocus: () => void;
  /** Receives the canonical `HH:mm` (24h) value. */
  onTimeChange: (value: string) => void;
}

export function DateTimeInputGroup({
  field,
  activeField,
  dateTestId,
  timeTestId,
  displayDate,
  time,
  dateFormat,
  timeFormat,
  disabled,
  includeTime,
  timeLabelKey,
  onDateChange,
  onDateBlur,
  onDateFocus,
  onTimeChange,
}: DateTimeInputGroupProps) {
  const { t } = useTranslation('tasks');

  return (
    <div className={cn('flex gap-1.5', includeTime && 'flex-col sm:flex-row sm:items-center')}>
      <Input
        data-testid={dateTestId}
        value={displayDate}
        disabled={disabled}
        onChange={onDateChange}
        onBlur={onDateBlur}
        onFocus={onDateFocus}
        placeholder={dateFormat}
        className={cn(
          'h-7 min-w-0 flex-1 text-xs',
          activeField === field && 'ring-1 ring-primary ring-offset-0',
        )}
      />
      {includeTime && (
        <>
          <p className="text-[10px] text-muted-foreground sm:hidden">{t(timeLabelKey)}</p>
          <TimeSelect
            testId={timeTestId}
            value={time || null}
            timeFormat={timeFormat}
            disabled={disabled}
            onChange={onTimeChange}
          />
        </>
      )}
    </div>
  );
}
